import type {
  ScheduleExpectation,
  ChronologicalScheduleExpectation
} from '@/types/scheduleExpectation';

export type CancelSwitchAvailability =
  | { status: 'available'; originatingFutureNodeId: string; counterpartFutureNodeId: string }
  | { status: 'disabled'; reason: string };

const CANT_RESTORE_REASON =
  "Can't automatically cancel this change because it also moved an existing expectation's end date. Contact support to undo it.";
const NOT_FOUND_REASON = "Can't automatically cancel this change right now.";

const findFutureCustomNode = (
  nodes: ChronologicalScheduleExpectation[] | undefined,
  pendingStartAt: string
) =>
  (nodes ?? []).find(
    (node) => node.object_type === 'CustomScheduleExpectation' && node.start_at === pendingStartAt
  );

// Only the branches of the switch that purely *created* a not-yet-started
// custom expectation (rather than end-dating an existing one) can be undone --
// there's no API to restore an expectation's end date once moved. See A1/A3
// and B2/B3 in AP-8903's ticket description.
export const getCancelSwitchAvailability = ({
  pendingStartAt,
  originatingExpectation,
  originatingNodes,
  counterpartExpectation,
  counterpartNodes
}: {
  pendingStartAt: string;
  originatingExpectation: ScheduleExpectation | null | undefined;
  originatingNodes: ChronologicalScheduleExpectation[] | undefined;
  counterpartExpectation: ScheduleExpectation | null | undefined;
  counterpartNodes: ChronologicalScheduleExpectation[] | undefined;
}): CancelSwitchAvailability => {
  const originatingLegEndedExisting =
    !!originatingExpectation?.custom_schedule_expectation_id &&
    originatingExpectation.end_at === pendingStartAt;
  const counterpartLegEndedExisting =
    !!counterpartExpectation?.custom_schedule_expectation_id &&
    counterpartExpectation.end_at === pendingStartAt;

  if (originatingLegEndedExisting || counterpartLegEndedExisting) {
    return { status: 'disabled', reason: CANT_RESTORE_REASON };
  }

  const originatingNode = findFutureCustomNode(originatingNodes, pendingStartAt);
  const counterpartNode = findFutureCustomNode(counterpartNodes, pendingStartAt);
  if (!originatingNode || !counterpartNode) {
    return { status: 'disabled', reason: NOT_FOUND_REASON };
  }

  return {
    status: 'available',
    originatingFutureNodeId: originatingNode.object_id,
    counterpartFutureNodeId: counterpartNode.object_id
  };
};
